import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { MeetingsStatus, StatusResponse } from '../../../shared/api.js';
import { api } from '../lib/api';
import { formatDateTime, formatWeekday } from '../lib/format';
import { UnpaidPaymentsCard } from './Apartments';
import { CalendarsCard, UpcomingCard, useHiddenCalendars } from './CalendarCards';

export function Badge({ ok, children }: { ok: boolean; children: string }) {
  return <span className={`pill pill--${ok ? 'ok' : 'warn'}`}>{children}</span>;
}

/** Configured is not the same as connected: the badge follows the actual calendar request. */
function useConnection(configured: boolean): { label: string; ok: boolean; error?: string } {
  const query = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api.calendars(),
    enabled: configured,
  });
  if (!configured) return { label: 'Не настроен', ok: false };
  if (query.isPending) return { label: 'Проверка…', ok: false };
  if (query.isError) return { label: 'Ошибка', ok: false, error: query.error.message };
  return { label: 'Подключён', ok: true };
}

export function VarList({ missing, invalid }: { missing: string[]; invalid: string[] }) {
  if (missing.length === 0 && invalid.length === 0) return null;
  return (
    <ul className="var-list">
      {missing.map((name) => (
        <li key={name}>
          <code>{name}</code> <span className="muted">не задана</span>
        </li>
      ))}
      {invalid.map((problem) => (
        <li key={problem} className="text-error">
          {problem}
        </li>
      ))}
    </ul>
  );
}

function MeetingsCard({ status, tz }: { status: MeetingsStatus; tz: string }) {
  const query = useQuery({
    queryKey: ['meetings'],
    queryFn: () => api.meetings(),
    refetchInterval: 60_000,
  });
  const zoom = status.providers.find((p) => p.name === 'zoom');
  const meetings = query.data?.meetings ?? [];

  return (
    <article className="card">
      <header className="card__header">
        <h3>Встречи Zoom</h3>
        <Badge ok={Boolean(zoom?.configured)}>
          {zoom?.configured ? 'Настроен' : 'Не настроен'}
        </Badge>
      </header>
      {zoom?.configured ? (
        <p className="muted">
          Агент создаёт встречи с залом ожидания и кодом доступа. Хост — владелец приложения Zoom.
        </p>
      ) : (
        <p className="muted">
          Нужно приложение Server-to-Server OAuth в Zoom Marketplace (README, раздел Zoom):
        </p>
      )}
      {zoom && <VarList missing={zoom.missing} invalid={zoom.invalid} />}
      {status.warnings.map((w) => (
        <p key={w} className="text-error small">
          {w}
        </p>
      ))}
      {meetings.length > 0 && (
        <ul className="event-list">
          {meetings.slice(0, 8).map((m) => (
            <li key={m.id} className="event-row">
              <span className="event-row__time">{m.start ? formatDateTime(m.start, tz) : '—'}</span>
              <span className="event-row__title">
                {m.deleted ? (
                  <s>{m.topic ?? m.id}</s>
                ) : (
                  <a href={m.joinUrl} target="_blank" rel="noreferrer">
                    {m.topic ?? m.id}
                  </a>
                )}
              </span>
              <span className="event-row__meta muted small">
                {m.durationMin ? `${m.durationMin} мин` : ''}
                {m.createdBy?.startsWith('user:') && ` · ${m.createdBy.slice(5)}`}
                {m.deleted && ' · удалена'}
              </span>
            </li>
          ))}
        </ul>
      )}
      {zoom?.configured && query.data && meetings.length === 0 && (
        <p className="muted small">Агент ещё не создавал встреч.</p>
      )}
    </article>
  );
}

const ROADMAP = [
  { title: 'Скелет, база, веб-интерфейс', done: true },
  { title: 'Чтение календарей: события, поиск, дубли, конфликты', done: true },
  { title: 'Создание, изменение и удаление событий', done: true },
  { title: 'Локальная копия и синхронизация', done: true },
  { title: 'Встречи Zoom', done: true },
  { title: 'Звонок одной командой: встреча и событие', done: false },
];

export function Overview({ status }: { status: StatusResponse }) {
  const { calendar } = status;
  const tz = calendar.defaultTz;
  const connection = useConnection(calendar.configured);
  const [hiddenCalendars, toggleCalendar] = useHiddenCalendars();
  // Same query as the connection badge and the calendars card: one request.
  const calendars = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api.calendars(),
    enabled: calendar.configured,
  });
  const calendarNames = new Map((calendars.data?.calendars ?? []).map((c) => [c.id, c.name]));
  const queryClient = useQueryClient();
  const retry = () => {
    void queryClient.resetQueries({ queryKey: ['calendars'] });
    void queryClient.resetQueries({ queryKey: ['events'] });
  };

  return (
    <div className="page">
      <section className="hero">
        <div className="hero__label">Сейчас · {tz}</div>
        <div className="hero__time">{formatDateTime(calendar.now, tz)}</div>
        <div className="hero__weekday">{formatWeekday(calendar.now, tz)}</div>
      </section>

      <div className="grid">
        {calendar.configured && !connection.error && (
          <>
            <UpcomingCard tz={tz} hidden={hiddenCalendars} />
            <CalendarsCard hidden={hiddenCalendars} onToggle={toggleCalendar} />
          </>
        )}

        <article className="card">
          <header className="card__header">
            <h3>Календарь iCloud</h3>
            <Badge ok={connection.ok}>{connection.label}</Badge>
          </header>
          {connection.error ? (
            <>
              <p className="text-error">{connection.error}</p>
              <button type="button" className="btn btn--small" onClick={retry}>
                Повторить
              </button>
            </>
          ) : calendar.configured ? (
            <p className="muted">Учётные данные заданы. Выше — что видит агент.</p>
          ) : (
            <p className="muted">Заполните переменные в .env и перезапустите сервер:</p>
          )}
          <VarList missing={calendar.missing} invalid={calendar.invalid} />
          <dl className="facts">
            <dt>Запись</dt>
            <dd>
              {calendar.writableCalendars.length > 0 ? (
                calendar.writableCalendars.map((id) => (
                  <span key={id} className="block">
                    {calendarNames.get(id) ?? 'календарь не найден'}
                  </span>
                ))
              ) : (
                <span className="muted">запрещена</span>
              )}
            </dd>
          </dl>
        </article>

        <UnpaidPaymentsCard />

        {status.meetings && <MeetingsCard status={status.meetings} tz={tz} />}

        <article className="card">
          <header className="card__header">
            <h3>Этапы</h3>
            <span className="muted small">v{calendar.version}</span>
          </header>
          <ol className="roadmap">
            {ROADMAP.map((step) => (
              <li key={step.title} className={step.done ? 'roadmap__done' : undefined}>
                {step.title}
              </li>
            ))}
          </ol>
        </article>
      </div>
    </div>
  );
}
